<script lang="ts" setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/20/solid';
import { faPaperPlane, faSparkles } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { sendEditorialLead } from '@/api';
import { isValidationProblemDetails } from '@/common/api.ts';
import ResponsiveTurnstileWidget from '@/components/ResponsiveTurnstileWidget.vue';

const props = defineProps<{
  context?: string;
}>();

const isOpen = defineModel<boolean>('open', { required: true });

const { t } = useI18n();

const showForm = ref(true);
const isLoading = ref(false);
const responseError = ref('');

const name = reactive({ value: '', valid: true, error: '' });
const email = reactive({ value: '', valid: true, error: '' });
const telephone = reactive({ value: '', valid: true, error: '' });
const searchLocation = ref(props.context ?? '');
const propertyType = ref('');
const transactionType = ref('');
const budget = ref('');
const bedrooms = ref('');
const bathrooms = ref('');
const timeline = ref('');
const preferredContact = ref('');
const comments = ref('');
const consent = reactive({ value: false, valid: true, error: '' });
const token = reactive({ value: '', valid: false, error: '' });
const turnstileWidget = ref<InstanceType<typeof ResponsiveTurnstileWidget> | null>(null);

const resetTurnstile = () => {
  token.value = '';
  turnstileWidget.value?.reset();
};

const closeModal = () => {
  isOpen.value = false;
};

const isValidEmail = (value: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value);
};

const isValidTelephone = (value: string) => {
  const digits = value.replace(/\D/g, '');
  const normalizedDigits = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return normalizedDigits.length === 10;
};

const validateName = () => {
  if (name.value.trim().length === 0) {
    name.valid = false;
    name.error = t('views.editorial.help-widget.validation.name-required');
  } else {
    name.valid = true;
    name.error = '';
  }
};

const validateEmail = () => {
  if (!email.value.trim()) {
    email.valid = false;
    email.error = t('views.editorial.help-widget.validation.email-required');
  } else if (!isValidEmail(email.value)) {
    email.valid = false;
    email.error = t('views.editorial.help-widget.validation.email-invalid');
  } else {
    email.valid = true;
    email.error = '';
  }
};

const validateTelephone = () => {
  if (!telephone.value.trim()) {
    telephone.valid = false;
    telephone.error = t('views.editorial.help-widget.validation.telephone-required');
  } else if (!isValidTelephone(telephone.value)) {
    telephone.valid = false;
    telephone.error = t('views.editorial.help-widget.validation.telephone-invalid');
  } else {
    telephone.valid = true;
    telephone.error = '';
  }
};

const validateConsent = () => {
  if (!consent.value) {
    consent.valid = false;
    consent.error = t('views.editorial.help-widget.validation.consent-required');
  } else {
    consent.valid = true;
    consent.error = '';
  }
};

const validateToken = () => {
  if (!token.value) {
    token.valid = false;
    token.error = t('views.editorial.help-widget.validation.captcha-failed');
  } else {
    token.valid = true;
    token.error = '';
  }
};

const handleTurnstileSuccess = (value: string) => {
  token.value = value;
};

const handleTurnstileError = (error: string) => {
  token.value = '';
  console.error('Turnstile error:', error);
};

const handleTurnstileExpired = () => {
  token.value = '';
};

const validateForm = () => {
  validateName();
  validateEmail();
  validateTelephone();
  validateConsent();
  validateToken();
};

const isFormValid = computed(() => name.valid && email.valid && telephone.valid && consent.valid && token.valid);

const handleSubmit = async () => {
  responseError.value = '';
  validateForm();
  if (!isFormValid.value) {
    return;
  }

  isLoading.value = true;
  try {
    const result = await sendEditorialLead({
      body: {
        name: name.value,
        email: email.value,
        telephone: telephone.value,
        searchLocation: searchLocation.value || null,
        propertyType: propertyType.value || null,
        transactionType: transactionType.value || null,
        budget: budget.value || null,
        bedrooms: bedrooms.value || null,
        bathrooms: bathrooms.value || null,
        timeline: timeline.value || null,
        preferredContact: preferredContact.value || null,
        comments: comments.value || null,
        context: props.context || null,
        consent: consent.value,
        token: token.value,
      },
    });

    if (result.response?.ok) {
      showForm.value = false;
      return;
    }

    if (result.error && isValidationProblemDetails(result.error) && result.error.errors) {
      let hasValidationErrors = false;
      for (const key in result.error.errors) {
        if (!result.error.errors[key] || !result.error.errors[key][0]) continue;
        switch (key) {
          case 'name':
            name.valid = false;
            name.error = result.error.errors[key][0];
            hasValidationErrors = true;
            break;
          case 'email':
            email.valid = false;
            email.error = result.error.errors[key][0];
            hasValidationErrors = true;
            break;
          case 'telephone':
            telephone.valid = false;
            telephone.error = result.error.errors[key][0];
            hasValidationErrors = true;
            break;
          case 'consent':
            consent.valid = false;
            consent.error = result.error.errors[key][0];
            hasValidationErrors = true;
            break;
          case 'token':
            token.valid = false;
            token.error = result.error.errors[key][0];
            hasValidationErrors = true;
            break;
        }
      }

      if (hasValidationErrors) {
        resetTurnstile();
        return;
      }
    }

    resetTurnstile();
    responseError.value = t('views.editorial.help-widget.error');
  } catch {
    resetTurnstile();
    responseError.value = t('views.editorial.help-widget.error');
  } finally {
    isLoading.value = false;
  }
};

watch(() => name.value, validateName);
watch(() => email.value, validateEmail);
watch(() => telephone.value, validateTelephone);
watch(() => consent.value, validateConsent);
watch(() => token.value, validateToken);
watch(
  () => props.context,
  (context, previousContext) => {
    if (searchLocation.value !== (previousContext ?? '')) return;

    searchLocation.value = context ?? '';
  }
);
</script>

<template>
  <TransitionRoot :show="isOpen" as="template">
    <Dialog class="relative z-10" @close="closeModal">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500/75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel class="relative w-full transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:max-w-lg sm:p-6">
              <form v-if="showForm" class="grid gap-4" @submit.prevent="handleSubmit">
                <DialogTitle as="h3" class="text-lg font-semibold text-gray-900">{{ t('views.editorial.help-widget.modal-title') }}</DialogTitle>
                <p class="text-sm text-gray-500">{{ t('views.editorial.help-widget.modal-description') }}</p>

                <div class="relative mt-1">
                  <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900" for="help-name">{{ t('views.editorial.help-widget.name.label') }}</label>
                  <input
                    id="help-name"
                    v-model="name.value"
                    :class="[
                      !name.valid ? 'text-red-900 outline-red-300 focus:outline-red-600' : 'focus:outline-ph-green-600 text-gray-900 outline-gray-300',
                      'block w-full rounded-md border-none bg-white px-3 py-1.5 text-base outline-1 -outline-offset-2 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6',
                    ]"
                    :placeholder="t('views.editorial.help-widget.name.placeholder')"
                    name="help-name"
                    type="text"
                    @keydown.enter.prevent
                  />
                  <div v-if="!name.valid" class="py-1 pl-2 text-xs text-red-900">{{ name.error }}</div>
                </div>

                <div class="relative">
                  <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900" for="help-email">{{ t('views.editorial.help-widget.email.label') }}</label>
                  <input
                    id="help-email"
                    v-model="email.value"
                    :class="[
                      !email.valid ? 'text-red-900 outline-red-300 focus:outline-red-600' : 'focus:outline-ph-green-600 text-gray-900 outline-gray-300',
                      'block w-full rounded-md border-none bg-white px-3 py-1.5 text-base outline-1 -outline-offset-2 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6',
                    ]"
                    :placeholder="t('views.editorial.help-widget.email.placeholder')"
                    name="help-email"
                    type="text"
                    @keydown.enter.prevent
                  />
                  <div v-if="!email.valid" class="py-1 pl-2 text-xs text-red-900">{{ email.error }}</div>
                </div>

                <div class="relative">
                  <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900" for="help-telephone">{{ t('views.editorial.help-widget.telephone.label') }}</label>
                  <input
                    id="help-telephone"
                    v-model="telephone.value"
                    :class="[
                      !telephone.valid ? 'text-red-900 outline-red-300 focus:outline-red-600' : 'focus:outline-ph-green-600 text-gray-900 outline-gray-300',
                      'block w-full rounded-md border-none bg-white px-3 py-1.5 text-base outline-1 -outline-offset-2 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6',
                    ]"
                    :placeholder="t('views.editorial.help-widget.telephone.placeholder')"
                    name="help-telephone"
                    type="text"
                    @keydown.enter.prevent
                  />
                  <div v-if="!telephone.valid" class="py-1 pl-2 text-xs text-red-900">{{ telephone.error }}</div>
                </div>

                <div class="relative">
                  <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900" for="help-search-location">{{ t('views.editorial.help-widget.search-location.label') }}</label>
                  <input
                    id="help-search-location"
                    v-model="searchLocation"
                    class="focus:outline-ph-green-600 block w-full rounded-md border-none bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-2 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
                    :placeholder="t('views.editorial.help-widget.search-location.placeholder')"
                    name="help-search-location"
                    type="text"
                    @keydown.enter.prevent
                  />
                </div>

                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div class="relative">
                    <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900" for="help-property-type">{{ t('views.editorial.help-widget.property-type.label') }}</label>
                    <div class="grid grid-cols-1">
                      <select
                        id="help-property-type"
                        v-model="propertyType"
                        class="focus:outline-ph-green-600 col-start-1 row-start-1 w-full appearance-none rounded-md border-none bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-2 outline-gray-300 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
                        name="help-property-type"
                      >
                        <option value="">{{ t('views.editorial.help-widget.select-default') }}</option>
                        <option :value="t('views.editorial.help-widget.property-type.house')">{{ t('views.editorial.help-widget.property-type.house') }}</option>
                        <option :value="t('views.editorial.help-widget.property-type.condo')">{{ t('views.editorial.help-widget.property-type.condo') }}</option>
                        <option :value="t('views.editorial.help-widget.property-type.apartment')">{{ t('views.editorial.help-widget.property-type.apartment') }}</option>
                        <option :value="t('views.editorial.help-widget.property-type.plex')">{{ t('views.editorial.help-widget.property-type.plex') }}</option>
                        <option :value="t('views.editorial.help-widget.property-type.land')">{{ t('views.editorial.help-widget.property-type.land') }}</option>
                        <option :value="t('views.editorial.help-widget.property-type.other')">{{ t('views.editorial.help-widget.property-type.other') }}</option>
                      </select>
                      <ChevronDownIcon aria-hidden="true" class="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4" />
                    </div>
                  </div>

                  <div class="relative">
                    <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900" for="help-transaction-type">{{ t('views.editorial.help-widget.transaction-type.label') }}</label>
                    <div class="grid grid-cols-1">
                      <select
                        id="help-transaction-type"
                        v-model="transactionType"
                        class="focus:outline-ph-green-600 col-start-1 row-start-1 w-full appearance-none rounded-md border-none bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-2 outline-gray-300 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
                        name="help-transaction-type"
                      >
                        <option value="">{{ t('views.editorial.help-widget.select-default') }}</option>
                        <option :value="t('views.editorial.help-widget.transaction-type.buy')">{{ t('views.editorial.help-widget.transaction-type.buy') }}</option>
                        <option :value="t('views.editorial.help-widget.transaction-type.rent')">{{ t('views.editorial.help-widget.transaction-type.rent') }}</option>
                      </select>
                      <ChevronDownIcon aria-hidden="true" class="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4" />
                    </div>
                  </div>

                  <div class="relative">
                    <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900" for="help-bedrooms">{{ t('views.editorial.help-widget.bedrooms.label') }}</label>
                    <div class="grid grid-cols-1">
                      <select
                        id="help-bedrooms"
                        v-model="bedrooms"
                        class="focus:outline-ph-green-600 col-start-1 row-start-1 w-full appearance-none rounded-md border-none bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-2 outline-gray-300 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
                        name="help-bedrooms"
                      >
                        <option value="">{{ t('views.editorial.help-widget.select-default') }}</option>
                        <option value="1+">1+</option>
                        <option value="2+">2+</option>
                        <option value="3+">3+</option>
                        <option value="4+">4+</option>
                      </select>
                      <ChevronDownIcon aria-hidden="true" class="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4" />
                    </div>
                  </div>

                  <div class="relative">
                    <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900" for="help-bathrooms">{{ t('views.editorial.help-widget.bathrooms.label') }}</label>
                    <div class="grid grid-cols-1">
                      <select
                        id="help-bathrooms"
                        v-model="bathrooms"
                        class="focus:outline-ph-green-600 col-start-1 row-start-1 w-full appearance-none rounded-md border-none bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-2 outline-gray-300 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
                        name="help-bathrooms"
                      >
                        <option value="">{{ t('views.editorial.help-widget.select-default') }}</option>
                        <option value="1+">1+</option>
                        <option value="2+">2+</option>
                        <option value="3+">3+</option>
                      </select>
                      <ChevronDownIcon aria-hidden="true" class="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4" />
                    </div>
                  </div>

                  <div class="relative">
                    <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900" for="help-budget">{{ t('views.editorial.help-widget.budget.label') }}</label>
                    <input
                      id="help-budget"
                      v-model="budget"
                      class="focus:outline-ph-green-600 block w-full rounded-md border-none bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-2 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
                      :placeholder="t('views.editorial.help-widget.budget.placeholder')"
                      name="help-budget"
                      type="text"
                      @keydown.enter.prevent
                    />
                  </div>

                  <div class="relative">
                    <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900" for="help-timeline">{{ t('views.editorial.help-widget.timeline.label') }}</label>
                    <div class="grid grid-cols-1">
                      <select
                        id="help-timeline"
                        v-model="timeline"
                        class="focus:outline-ph-green-600 col-start-1 row-start-1 w-full appearance-none rounded-md border-none bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-2 outline-gray-300 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
                        name="help-timeline"
                      >
                        <option value="">{{ t('views.editorial.help-widget.select-default') }}</option>
                        <option :value="t('views.editorial.help-widget.timeline.immediately')">{{ t('views.editorial.help-widget.timeline.immediately') }}</option>
                        <option :value="t('views.editorial.help-widget.timeline.three-months')">{{ t('views.editorial.help-widget.timeline.three-months') }}</option>
                        <option :value="t('views.editorial.help-widget.timeline.six-months')">{{ t('views.editorial.help-widget.timeline.six-months') }}</option>
                        <option :value="t('views.editorial.help-widget.timeline.one-year')">{{ t('views.editorial.help-widget.timeline.one-year') }}</option>
                        <option :value="t('views.editorial.help-widget.timeline.flexible')">{{ t('views.editorial.help-widget.timeline.flexible') }}</option>
                      </select>
                      <ChevronDownIcon aria-hidden="true" class="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4" />
                    </div>
                  </div>
                </div>

                <div class="relative">
                  <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900" for="help-preferred-contact">{{ t('views.editorial.help-widget.preferred-contact.label') }}</label>
                  <div class="grid grid-cols-1">
                    <select
                      id="help-preferred-contact"
                      v-model="preferredContact"
                      class="focus:outline-ph-green-600 col-start-1 row-start-1 w-full appearance-none rounded-md border-none bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-2 outline-gray-300 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
                      name="help-preferred-contact"
                    >
                      <option value="">{{ t('views.editorial.help-widget.select-default') }}</option>
                      <option :value="t('views.editorial.help-widget.preferred-contact.phone')">{{ t('views.editorial.help-widget.preferred-contact.phone') }}</option>
                      <option :value="t('views.editorial.help-widget.preferred-contact.email')">{{ t('views.editorial.help-widget.preferred-contact.email') }}</option>
                      <option :value="t('views.editorial.help-widget.preferred-contact.either')">{{ t('views.editorial.help-widget.preferred-contact.either') }}</option>
                    </select>
                    <ChevronDownIcon aria-hidden="true" class="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4" />
                  </div>
                </div>

                <div class="relative">
                  <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900" for="help-comments">{{ t('views.editorial.help-widget.comments.label') }}</label>
                  <textarea
                    id="help-comments"
                    v-model="comments"
                    class="focus:outline-ph-green-600 block w-full rounded-md border-none bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-2 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
                    :placeholder="t('views.editorial.help-widget.comments.placeholder')"
                    name="help-comments"
                    rows="3"
                  />
                </div>

                <div class="flex gap-3">
                  <div class="flex h-6 shrink-0 items-center">
                    <input
                      id="help-consent"
                      v-model="consent.value"
                      class="checked:border-ph-green-600 checked:bg-ph-green-600 focus-visible:outline-ph-green-600 size-4 appearance-none rounded-sm border border-gray-300 bg-white focus-visible:outline-2 focus-visible:outline-offset-2"
                      name="help-consent"
                      type="checkbox"
                    />
                  </div>
                  <div class="text-sm/6">
                    <label class="text-gray-500" for="help-consent">{{ t('views.editorial.help-widget.consent') }}</label>
                    <div v-if="!consent.valid" class="text-xs text-red-900">{{ consent.error }}</div>
                  </div>
                </div>

                <div class="flex flex-col">
                  <ResponsiveTurnstileWidget ref="turnstileWidget" @error="handleTurnstileError" @expired="handleTurnstileExpired" @success="handleTurnstileSuccess" />
                  <div v-if="!token.valid" class="py-1 pl-2 text-center text-xs text-red-900">{{ token.error }}</div>
                </div>

                <div class="flex items-center justify-end gap-3">
                  <button class="cursor-pointer rounded-lg px-4 py-2 text-gray-600 hover:text-gray-900" type="button" @click="closeModal">
                    {{ t('views.editorial.help-widget.cancel') }}
                  </button>
                  <button :disabled="isLoading" class="hover:bg-ph-green-600 bg-ph-green-700 inline-block cursor-pointer rounded-lg px-4 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50" type="submit">
                    <FontAwesomeIcon :icon="faPaperPlane" class="text-ph-green-200 mr-1" />
                    {{ isLoading ? t('views.editorial.help-widget.sending') : t('views.editorial.help-widget.send') }}
                  </button>
                </div>

                <p class="text-center text-xs text-gray-600">
                  <span>{{ t('views.editorial.help-widget.privacy-consent') }}</span>
                  <RouterLink :to="{ name: 'privacy' }" class="hover:text-ph-green-700 underline">
                    {{ t('views.editorial.help-widget.privacy-policy') }}
                  </RouterLink>
                  <span>.</span>
                </p>

                <p v-if="responseError" class="text-center text-sm text-red-900">{{ responseError }}</p>
              </form>

              <div v-else class="flex flex-col items-center justify-center gap-6 p-10 text-center">
                <FontAwesomeIcon :icon="faSparkles" class="text-ph-green-700" size="3x" />
                <h3 class="text-lg font-semibold text-gray-900">{{ t('views.editorial.help-widget.success-title') }}</h3>
                <p class="text-sm text-gray-600">{{ t('views.editorial.help-widget.success-message') }}</p>
                <button class="hover:bg-ph-green-600 bg-ph-green-700 cursor-pointer rounded-lg px-4 py-2 text-white" type="button" @click="closeModal">
                  {{ t('views.editorial.help-widget.close') }}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<style lang="css" scoped></style>
